import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  FolderArchive, Download, Plus, Shield, FileCheck,
  Calendar, Lock
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { auditPacks, formatDate } from '../data/mockData';

const AuditPacksPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Generated': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Archived': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-packs-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Audit Packs
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Generate and manage compliance evidence packages with integrity verification
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Audit Packs Report"
            subtitle="Generate and manage compliance evidence packages with integrity verification"
          /> */}
          {!isReadOnly && (
            <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="generate-pack">
              <Plus className="h-4 w-4 mr-2" />
              Generate New Pack
            </Button>
          )}
        </div>
      </div>

      <ReportSection id="audit-packs-metrics" title="Audit Packs Metrics" description="Compliance evidence package statistics">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.13" req="SM-AUD-001" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-md">
                  <FolderArchive className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wider font-medium">Total Packs</p>
                  <p className="text-2xl font-bold text-slate-900">{auditPacks.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-md">
                  <FileCheck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wider font-medium">Evidence Files</p>
                  <p className="text-2xl font-bold text-blue-600">757</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-md">
                  <Lock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wider font-medium">Integrity Verified</p>
                  <p className="text-2xl font-bold text-purple-600">100%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <ReportSection id="audit-packs-list" title="Audit Packs List" description="All compliance evidence packages">
        <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §6.13" req="SM-DI-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Audit Packs</CardTitle>
          </CardHeader>
          <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="text-slate-600">ID</TableHead>
              <TableHead className="text-slate-600">Name</TableHead>
              <TableHead className="text-slate-600">Status</TableHead>
              <TableHead className="text-slate-600">Scope</TableHead>
              <TableHead className="text-slate-600">Generated</TableHead>
              <TableHead className="text-slate-600">Evidence Count</TableHead>
              <TableHead className="text-slate-600">Integrity</TableHead>
              <TableHead className="text-slate-600">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditPacks.map(pack => (
              <TableRow key={pack.id} className="hover:bg-slate-50" data-testid={`pack-row-${pack.id}`}>
                <TableCell className="font-mono text-xs text-slate-500">{pack.id}</TableCell>
                <TableCell className="text-sm text-slate-800 font-medium">{pack.name}</TableCell>
                <TableCell>
                  <Badge className={`${getStatusColor(pack.status)} text-xs`}>{pack.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {pack.scope.plant} • {pack.scope.period}
                </TableCell>
                <TableCell className="text-sm text-slate-600">{formatDate(pack.generatedAt)}</TableCell>
                <TableCell className="text-sm text-slate-800 font-medium">{pack.evidenceCount}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Shield className="h-3 w-3 text-emerald-600" />
                    <span className="text-xs text-emerald-600 font-mono">SHA256</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                    <Download className="h-3 w-3 mr-1" />
                    Export
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      </ReportSection>

      <Card className="bg-blue-50 border border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Data Integrity Assurance</p>
              <p className="text-xs text-blue-700 mt-1">
                All audit packs are cryptographically signed with SHA-256 hash for immutability verification. 
                Evidence files include complete audit trails per ALCOA+ principles.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditPacksPage;
